import React, { useState } from 'react';
import { Button } from '../UI/Button';
import { X, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

export type ContractActionType = 'ACCEPT' | 'REJECT' | 'CANCEL';

interface ContractActionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason?: string) => void;
    type: ContractActionType;
    title: string;
    contractTitle: string;
}

export const ContractActionModal: React.FC<ContractActionModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    type,
    title,
    contractTitle,
}) => {
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        if ((type === 'REJECT' || type === 'CANCEL') && !reason.trim()) {
            return;
        }

        setIsSubmitting(true);
        try {
            await onConfirm(reason);
            onClose();
        } catch (error) {
            console.error('Action failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'ACCEPT':
                return <CheckCircle className="w-12 h-12 text-green-500 mb-4" />;
            case 'REJECT':
                return <XCircle className="w-12 h-12 text-red-500 mb-4" />;
            case 'CANCEL':
                return <AlertTriangle className="w-12 h-12 text-orange-500 mb-4" />;
        }
    };

    const getDescription = () => {
        switch (type) {
            case 'ACCEPT':
                return `你确定要接受合同 "${contractTitle}" 吗？接受后项目将正式开始执行。`;
            case 'REJECT':
                return `你确定要拒绝合同 "${contractTitle}" 吗？拒绝后合同将无法继续。请填写拒绝原因。`;
            case 'CANCEL':
                return `你确定要取消合同 "${contractTitle}" 吗？取消后项目将终止。请填写取消原因。`;
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex justify-end p-4">
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-8 pb-8 flex flex-col items-center text-center">
                    {getIcon()}

                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                        {title}
                    </h3>

                    <p className="text-slate-600 mb-6">
                        {getDescription()}
                    </p>

                    {(type === 'REJECT' || type === 'CANCEL') && (
                        <div className="w-full mb-6">
                            <label className="block text-sm font-medium text-slate-700 mb-2 text-left">
                                {type === 'REJECT' ? '拒绝原因' : '取消原因'} <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder={type === 'REJECT' ? "请输入拒绝原因..." : "请输入取消原因..."}
                                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 min-h-[100px] resize-none"
                            />
                        </div>
                    )}

                    <div className="flex w-full gap-3">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="flex-1"
                        >
                            再想想
                        </Button>
                        <Button
                            variant={type === 'ACCEPT' ? 'primary' : 'danger'}
                            onClick={handleSubmit}
                            className="flex-1"
                            disabled={(type !== 'ACCEPT' && !reason.trim()) || isSubmitting}
                        >
                            {isSubmitting ? '处理中...' : '确定'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
